import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseScopeChangeReason,
  ReleaseScopeItem,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { REASON_REQUIRING_COMMENT, SCOPE_CHANGE_REASONS } from "./releaseVocabulary";

/**
 * Taking work out of a release (§14).
 *
 * <p>The reason is required whether or not the scope was frozen, which is stricter than adding.
 * There is no version of a removal that leaves the record self-explanatory: what fell out of a
 * release and why is the most-read page of any release post-mortem, and a blank there is exactly
 * where its question is.
 */
export function RemoveScopeDrawer({
  release,
  item,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  item: ReleaseScopeItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState<ReleaseScopeChangeReason | "">("");
  const [comment, setComment] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (item) {
      setReason("");
      setComment("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  if (!item) {
    return null;
  }

  const needsComment = reason !== "" && REASON_REQUIRING_COMMENT.includes(reason);
  const canSubmit = reason !== "" && (!needsComment || comment.trim() !== "");

  const submit = () =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/scope/items/${item.workItemId}/removal`, {
          relationType: item.relationType,
          reason,
          comment: comment.trim() === "" ? null : comment.trim(),
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open
      onClose={onClose}
      title="Take this out of the release"
      subtitle={`${item.itemKey} — ${item.title}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="danger" onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Removing…" : "Remove from release"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <p className="text-sm text-ink-600">
          The work item is untouched — it simply stops being part of this release. The record of it
          having been in, and why it came out, is kept.
        </p>

        {release.scopeFrozenAt && (
          <InlineAlert tone="warning">
            The scope was frozen. This will show on the release as a change to a settled scope.
          </InlineAlert>
        )}

        <Field label="Why is it coming out" required>
          <Select
            value={reason}
            onChange={(event) => setReason(event.target.value as ReleaseScopeChangeReason | "")}
          >
            <option value="">Choose a reason…</option>
            {SCOPE_CHANGE_REASONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="What happened"
          required={needsComment}
          hint={needsComment ? "\"Something else\" needs saying what it was." : "Optional."}
        >
          <Textarea
            rows={3}
            value={comment}
            placeholder="Regression found in the payment flow; moving to 2.2."
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
